'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { getLogger, LogCategory, LogEvent, LogLevel } from '../../../infrastructure/logger';
const logger = getLogger('ui');

/** How long a spinner may stay up before it is worth reporting. */
const STUCK_SPINNER_WARNING_MS = 15_000;

/**
 * How long a source may be absent before its clock restarts. A regenerated tree
 * remounts everything, and a per-mount timer would restart with it — so a page
 * remounting every few seconds would never reach the threshold.
 */
const REMOUNT_GRACE_MS = 2_000;

/** How often the age is written to the DOM and the threshold re-checked. */
const PROGRESS_TICK_MS = 2_000;

/** How often one stuck source repeats before going quiet. */
const MAX_STUCK_REPORTS = 4;

/** When each source's spinner was first seen, kept across remounts. */
const firstSeenBySource = new Map<string, number>();
/** Reports sent per source, so a stuck page repeats but a remount loop does not. */
const reportCountBySource = new Map<string, number>();

/**
 * Reports a spinner that never clears, and keeps its age in `data-spinner-ms`.
 *
 * The attribute is the part worth keeping: unlike a log it survives any log
 * level or reporter, so a missing one means the effect never ran and a frozen
 * one means the page's timers stopped. Both were the answer at different points.
 */
export function useStuckSpinnerReport(source, container) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== container || $[1] !== source) {
    t0 = () => {
      const key = source ?? "unlabelled";
      const seenAt = firstSeenBySource.get(key) ?? Date.now();
      firstSeenBySource.set(key, seenAt);
      const mark = () => {
        container.current?.setAttribute("data-spinner-ms", String(Date.now() - seenAt));
      };
      const report = () => {
        const sent = reportCountBySource.get(key) ?? 0;
        if (sent >= MAX_STUCK_REPORTS) {
          return;
        }
        reportCountBySource.set(key, sent + 1);
        logger.contextualLog(LogLevel.WARN, `[Loading] "${key}" on screen for ${Date.now() - seenAt}ms at ${window.location.pathname}`, undefined, {
          category: LogCategory.LEGACY,
          event: LogEvent.LEGACY_LOG
        });
      };
      const tick = () => {
        mark();
        if (Date.now() - seenAt >= STUCK_SPINNER_WARNING_MS) {
          report();
        }
      };
      tick();
      const ticker = setInterval(tick, PROGRESS_TICK_MS);
      return () => {
        clearInterval(ticker);
        setTimeout(() => {
          if (firstSeenBySource.get(key) === seenAt) {
            firstSeenBySource.delete(key);
            reportCountBySource.delete(key);
          }
        }, REMOUNT_GRACE_MS);
      };
    };
    t1 = [source, container];
    $[0] = container;
    $[1] = source;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  React.useEffect(t0, t1);
}